import {
  theme as defaultTheme,
  Divider,
  Filter,
  Icon,
  useSnackbar,
} from "@mindlercare/ui-web";
import { Tab, Tabs, Tooltip } from "@mui/material";
import Dialog from "@mui/material/Dialog/Dialog";
import {
  IcbtQuestionnaires,
  IcbtTranslationProvider,
  ProgramPages,
} from "@mindlercare/icbt";
import { FeedbackLockReason, SequentialUnlockItem } from "api/types";
import { useGetAllProgress, useGetIcbtResponses } from "api/useIcbtQueries";
import { Questionnaire } from "components/icbt/programs/ContentfulRichText/Questionnaire";
import { useIcbtTranslations } from "components/icbt/hooks/useIcbtTranslations";
import { StyledDialogTitle } from "components/lib/customModal/customModal.styles";
import { Typography } from "components/typography";

import { LocalSettingKey, getLocalSetting } from "core/localSettings";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import {
  ProgramsAndTreatments,
  useGetAllProgramsAndTreatmentsQuery,
} from "../../../../../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";
import { useGetProgramContentQuery } from "../../../../../../hooks/icbt/useGetProgramContentQuery";
import { AddButton } from "../../AddButton";
import { CopyToClipboard } from "./CopyToClipboard";
import { FeedbackForm } from "./FeedbackForm";
import { Notes } from "./Notes";
import { PatientNoteResponse } from "./PatientNoteResponse";
import { LockIcon } from "../../useIcbtTableData/styles";
import { LEGACY_NOTE_ID } from "../../constants";
import { useFlag } from "@mindlercare/unleash-client";
import { AssessmentForm } from "./AssessmentForm";
import { FeedbackHistory } from "./FeedbackHistory";

const FixedContainer = styled.div`
  position: fixed;
  background-color: ${defaultTheme.COLORS.WHITE};
  inset: 0;
  width: 90vw;
  height: 90vh;
  margin: auto;
  overflow: hidden;
  display: grid;
  grid-template-rows: auto 1fr;
  padding: 24px 40px 40px 40px;
`;

const HeaderContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: auto auto;
`;

const CloseDialogButton = styled.button`
  all: unset;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-left: 8px;
  & > button {
    margin: 0px;
  }
`;

const FlexContainer = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: ${defaultTheme.SPACING.XL};
  padding-top: ${defaultTheme.SPACING.LG};
`;

const SubtextContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AssignmentIcon = styled(Icon.Assignment)`
  color: ${(props) => props.theme.COLORS.GRAY};
`;

const Container = styled.div`
  position: relative;
`;

const Wrapper = styled.div`
  box-sizing: border-box;
  position: absolute;
  inset: 0;
  border-right: 1px solid ${defaultTheme.COLORS.GRAY_MEDIUM};
`;

const ScrollableWrapper = styled.div`
  height: 100%;
  overflow-y: auto;
  // this advanced calculus makes sure that scrollbar is above the right border
  // of the Wrapper and the content is padded from that border by the same amount
  // as the gap of the FlexContainer.
  margin-right: calc(-1 * (${defaultTheme.SPACING.SM}));
  padding-right: calc(${defaultTheme.SPACING.XL} + ${defaultTheme.SPACING.SM});

  ::-webkit-scrollbar {
    width: ${defaultTheme.SPACING.MD};
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${defaultTheme.COLORS.GRAY_MEDIUM};
    border-radius: 8px; /* Increased for the wider track */
    border: 4px solid transparent; /* This creates space around the thumb */
    background-clip: padding-box; /* This ensures the background color doesn't extend into the border */
  }

  ::-webkit-scrollbar-track {
    background-color: ${defaultTheme.COLORS.GRAY_X_LIGHT};
    border-radius: 4px; /* Match the thumb's border-radius */
  }
`;

interface Props {
  handleCloseFeedbackModal: () => void;
  open: boolean;
  treatmentId: string;
  programId: string;
  userId: number;
  icbtRequestId: string;
  icbtFeedbackDate?: string;
  icbtFeedbackDateIgnored?: boolean;
  patientName: string;
  patientSsn: string | null;
  setAssignIcbtModalIsOpen: (open: boolean) => void;
  isLocked?: boolean;
  isFeedbackLocked?: boolean;
  feedbackLockReason?: FeedbackLockReason;
  isRequestOwner?: boolean;
  requestingPsychologistId?: string;
  assessment?: SequentialUnlockItem["assessment"];
}

export const FeedbackModal = ({
  handleCloseFeedbackModal,
  open,
  treatmentId,
  programId,
  userId,
  icbtRequestId,
  icbtFeedbackDate,
  icbtFeedbackDateIgnored,
  patientName,
  patientSsn,
  setAssignIcbtModalIsOpen,
  isLocked,
  isFeedbackLocked,
  feedbackLockReason,
  isRequestOwner,
  requestingPsychologistId,
  assessment,
}: Props) => {
  const { t } = useTranslation();
  const isAssesmentEnabled = useFlag("ICBT_ASSESSMENT_FIELD");

  const icbtTranslations = useIcbtTranslations();

  const [showProgress, setShowProgress] = useState(true);
  const { data: allProgress } = useGetAllProgress(String(userId), {
    enabled: showProgress,
  });
  const currentProgressPageIndex = showProgress
    ? allProgress?.[programId]?.currentPageIndex
    : undefined;
  const [activeTab, setActiveTab] = useState(0);
  const { data: programsAndTreatments } =
    useGetAllProgramsAndTreatmentsQuery(undefined);

  const { data: programContent } = useGetProgramContentQuery({
    programId,
  });

  // Patient notes for this request, keyed by noteId so each Contentful-placed
  // PatientIcbtNote block can be paired with its saved response when rendered
  // inline. (React Query dedupes this with the same call inside <Notes>.)
  const { data: noteResponses } = useGetIcbtResponses({
    userId,
    icbtRequestId,
  });
  const responsesByNoteId = useMemo(
    () =>
      Object.fromEntries(
        (noteResponses ?? []).map((response) => [response.note_id, response]),
      ),
    [noteResponses],
  );

  const { programs, treatments } =
    programsAndTreatments ??
    ({
      programs: {},
      treatments: {},
    } as ProgramsAndTreatments);

  const programName = programs[programId]?.title;
  const treatmentName = treatments[treatmentId]?.title;

  const handleChangeActiveTab = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Pages that hold a reflection block the clinician should see inline: a
  // questionnaire, or a non-legacy patient note (the legacy note is shown
  // separately by <Notes>).
  const pagesWithReflectionBlocks =
    programContent?.pages.filter((page) => {
      return page.content.links.entries.block.some(
        (block) =>
          block.__typename === "Questionnaire" ||
          (block.__typename === "PatientIcbtNote" &&
            block.noteId !== LEGACY_NOTE_ID),
      );
    }) ?? [];

  // Title of the program page that holds the legacy reflection note, so it can
  // be labelled with its page heading like the inline notes are, instead of the
  // generic fallback question.
  const legacyNotePageTitle = programContent?.pages.find((page) =>
    page.content.links.entries.block.some(
      (block) =>
        block.__typename === "PatientIcbtNote" &&
        block.noteId === LEGACY_NOTE_ID,
    ),
  )?.title;

  const isReflectionsTabActive = activeTab === 0;
  const isAssignedProgramTabActive = activeTab === 1;

  return (
    <Dialog open={open} onClose={handleCloseFeedbackModal}>
      <FixedContainer>
        <HeaderContainer>
          <StyledDialogTitle>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Typography variant="h3" insensitive>
                {t("ICBT_FEEDBACK")}
              </Typography>
              {getLocalSetting(LocalSettingKey.ShowDebugInfo) && (
                <Tooltip
                  title={
                    <>
                      <div>{`icbtRequestId: ${icbtRequestId}`}</div>
                      <div>{`requestingPsychologistId: ${requestingPsychologistId ?? "—"}`}</div>
                    </>
                  }
                  placement="right"
                >
                  <span style={{ display: "flex" }}>
                    <Icon.Info width={16} height={16} />
                  </span>
                </Tooltip>
              )}
            </div>
            <SubtextContainer>
              {isLocked ? (
                <Tooltip
                  title={t("ICBT_LOCKED_TOOLTIP")}
                  placement="bottom-start"
                >
                  <div>{<LockIcon />}</div>
                </Tooltip>
              ) : (
                <AssignmentIcon />
              )}
              <Typography color="DARK_GREY" variant="body" insensitive>
                {treatmentName} · {programName}
              </Typography>
              <ButtonContainer>
                {isAssignedProgramTabActive && (
                  <Filter
                    isOn={showProgress}
                    onClick={() => setShowProgress((prev) => !prev)}
                  >
                    {t("SHOW_PROGRESS")}
                  </Filter>
                )}
                <AddButton
                  variant="secondary"
                  onClick={() => {
                    handleCloseFeedbackModal();
                    setAssignIcbtModalIsOpen(true);
                  }}
                  text={t("ASSIGN_ICBT")}
                />
                <CopyToClipboard
                  disabled={isAssignedProgramTabActive}
                  userId={userId}
                  icbtRequestId={icbtRequestId}
                  programName={programName}
                  patientName={patientName}
                />
              </ButtonContainer>
            </SubtextContainer>
          </StyledDialogTitle>
          <CloseDialogButton onClick={handleCloseFeedbackModal}>
            <Icon.Close />
          </CloseDialogButton>
          <Tabs value={activeTab} onChange={handleChangeActiveTab}>
            <Tab label={t("PATIENT_NOTES")} />
            <Tab label={t("ASSIGNED_PROGRAM")} />
          </Tabs>
        </HeaderContainer>
        <FlexContainer>
          <Container>
            <Wrapper>
              <ScrollableWrapper>
                {isReflectionsTabActive && (
                  <>
                    <Notes
                      icbtRequestId={icbtRequestId}
                      userId={userId}
                      legacyNotePageTitle={legacyNotePageTitle}
                    />
                    {pagesWithReflectionBlocks.length > 0 && (
                      <IcbtTranslationProvider translations={icbtTranslations}>
                        <IcbtQuestionnaires
                          pages={pagesWithReflectionBlocks}
                          renderQuestionnaire={(entry, opts) => (
                            <Questionnaire
                              key={entry.sys.id}
                              questionnaireType="icbt"
                              clinicalId={entry.clinicalId}
                              requestId={icbtRequestId}
                              patientUserId={userId}
                              variant={opts?.variant}
                            />
                          )}
                          renderPatientNote={(entry) =>
                            entry.noteId && entry.noteId !== LEGACY_NOTE_ID ? (
                              <PatientNoteResponse
                                description={entry.noteDescription}
                                response={responsesByNoteId[entry.noteId]}
                              />
                            ) : null
                          }
                        />
                      </IcbtTranslationProvider>
                    )}
                  </>
                )}
                {isAssignedProgramTabActive && (
                  <>
                    {programContent ? (
                      <ProgramPages
                        pages={programContent.pages}
                        currentProgressPageIndex={currentProgressPageIndex}
                        renderQuestionnaire={(entry) => (
                          <Questionnaire
                            key={entry.sys.id}
                            questionnaireType="icbt"
                            clinicalId={entry.clinicalId}
                            requestId={icbtRequestId}
                            patientUserId={userId}
                          />
                        )}
                        renderPatientNote={(entry) =>
                          entry.noteId && entry.noteId !== LEGACY_NOTE_ID ? (
                            <PatientNoteResponse
                              description={entry.noteDescription}
                              response={responsesByNoteId[entry.noteId]}
                            />
                          ) : null
                        }
                      />
                    ) : (
                      <ErrorMessage />
                    )}
                  </>
                )}
              </ScrollableWrapper>
            </Wrapper>
          </Container>
          <Container>
            <Wrapper>
              <ScrollableWrapper>
                <FeedbackForm
                  patientName={patientName}
                  userId={userId}
                  patientSsn={patientSsn}
                  icbtRequestId={icbtRequestId}
                  icbtFeedbackDate={icbtFeedbackDate}
                  icbtFeedbackDateIgnored={icbtFeedbackDateIgnored}
                  programId={programId}
                  isFeedbackLocked={isFeedbackLocked}
                  feedbackLockReason={feedbackLockReason}
                  isRequestOwner={isRequestOwner}
                />
                {isAssesmentEnabled && assessment != null ? (
                  <AssessmentForm
                    assessment={assessment}
                    icbtRequestId={icbtRequestId}
                  />
                ) : null}
                <FeedbackHistory
                  icbtRequestId={icbtRequestId}
                  userId={userId}
                />
              </ScrollableWrapper>
            </Wrapper>
          </Container>
        </FlexContainer>
      </FixedContainer>
    </Dialog>
  );
};

const ErrorMessage = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    enqueueSnackbar({
      text: t("SOMETHING_WENT_WRONG"),
    });
  }, [enqueueSnackbar, t]);

  return (
    <>
      <Typography variant="body" color="GRAY_BLACK">
        {t("NO_PROGRAMS_FOUND")}
      </Typography>
      <Divider />
    </>
  );
};
